import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { User } from '@ct-vision/shared';
import { authApi } from '@ct-vision/shared';
import type { ForgotPasswordRequest } from '@ct-vision/shared';

/**
 * 用户认证状态管理 Store
 * 管理用户登录状态、令牌和用户信息
 */
export const useAuthStore = defineStore('auth', () => {
  // ==================== State ====================
  
  /** 当前用户信息 */
  const user = ref<User | null>(null);
  /** 访问令牌 */
  const accessToken = ref<string | null>(localStorage.getItem('access_token'));
  /** 刷新令牌 */
  const refreshToken = ref<string | null>(localStorage.getItem('refresh_token'));
  /** 加载状态 */
  const loading = ref(false);

  // ==================== Getters ====================
  
  /** 是否已认证 */
  const isAuthenticated = computed(() => !!accessToken.value && !!user.value);
  /** 用户积分余额 */
  const userBalance = computed(() => user.value?.pointBalance ?? 0);

  // ==================== Actions ====================
  
  /**
   * 设置认证令牌
   * @param token 访问令牌
   * @param refresh 刷新令牌
   */
  const setTokens = (token: string, refresh: string) => {
    accessToken.value = token;
    refreshToken.value = refresh;
    localStorage.setItem('access_token', token);
    localStorage.setItem('refresh_token', refresh);
  };

  /**
   * 清除认证信息
   */
  const clearAuth = () => {
    user.value = null;
    accessToken.value = null;
    refreshToken.value = null;
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  };

  /**
   * 用户登录
   * @param phone 手机号
   * @param password 密码
   * @param inviteCode 邀请码（可选）
   * @returns 登录响应
   */
  const login = async (phone: string, password: string, inviteCode?: string) => {
    loading.value = true;
    try {
      const response = await authApi.login({ phone, password, inviteCode });
      setTokens(response.accessToken, response.refreshToken);
      user.value = response.user;
      return response;
    } finally {
      loading.value = false;
    }
  };

  /**
   * 用户注册
   * @param phone 手机号
   * @param password 密码
   * @param inviteCode 邀请码（可选）
   * @param verificationCode 短信验证码（可选）
   * @returns 注册响应
   */
  const register = async (phone: string, password: string, inviteCode?: string, verificationCode?: string) => {
    loading.value = true;
    try {
      const response = await authApi.register({ phone, password, inviteCode, verificationCode });
      setTokens(response.accessToken, response.refreshToken);
      user.value = response.user;
      return response;
    } finally {
      loading.value = false;
    }
  };

  /**
   * 忘记密码
   * @param phone 手机号
   * @param password 新密码
   * @param verificationCode 短信验证码（可选）
   * @returns 重置响应
   */
  const forgotPassword = async (phone: string, password: string, verificationCode?: string) => {
    loading.value = true;
    try {
      const response = await authApi.forgotPassword({ phone, password, verificationCode });
      setTokens(response.accessToken, response.refreshToken);
      user.value = response.user;
      return response;
    } finally {
      loading.value = false;
    }
  };

  /**
   * 获取当前用户信息
   */
  const fetchCurrentUser = async () => {
    if (!accessToken.value) return;
    try {
      const userData = await authApi.getCurrentUser();
      user.value = userData;
    } catch {
      clearAuth();
    }
  };

  /**
   * 从服务器刷新余额（消费扣费后调用）
   * 通过 walletApi 获取最新余额，更新本地状态
   */
  const refreshBalance = async () => {
    if (!accessToken.value || !user.value) return;
    try {
      const { walletApi } = await import('@ct-vision/shared');
      const data = await walletApi.getBalance();
      user.value.pointBalance = data.pointBalance;
    } catch (e) {
      console.error('刷新余额失败:', e);
    }
  };

  /**
   * 退出登录
   */
  const logout = () => {
    // 先捕获 token，再清除本地状态，最后用已捕获的 token 发出 logout 请求
    const token = accessToken.value;
    clearAuth();
    if (token) {
      authApi.logoutWithToken(token).catch(() => {});
    }
  };

  /**
   * 更新用户积分余额
   * @param points 变动的积分（正数增加，负数减少）
   */
  const updateBalance = (points: number) => {
    if (user.value) {
      user.value.pointBalance += points;
    }
  };

  return {
    user,
    accessToken,
    refreshToken,
    loading,
    isAuthenticated,
    userBalance,
    login,
    register,
    forgotPassword,
    fetchCurrentUser,
    logout,
    updateBalance,
    refreshBalance,
    setTokens,
    clearAuth,
  };
});
